import { AgencyTypeOptions, CreateAgencyRequest, UpdateAgencyRequest } from '../interfaces/types';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import axiosInstance, { ApiKeys, getApiUrl, logError } from '@cp/src/utils/ApiHelper';
import { Dispatch } from '@reduxjs/toolkit';
import {
  setAgencyData,
  setAgencyTypeMappings,
  setAgencyTypesData,
  setAllAgencyApiCalling,
  setAllAgencyData,
  setShowLoader,
  setStates
} from '@cp/src/reducers/AgencyDetailsSlice';
import { queryParam } from './constants';
import { showAddAgencyToast, showUpdateAgencyToast } from '../utils';

export const createNewAgency =
  (
    request: CreateAgencyRequest,
    setIsSubmitButtonDisabled: (value: boolean) => void,
    successCallback: () => void
  ) =>
  (dispatch: Dispatch) => {
    dispatch(setShowLoader(true));
    setIsSubmitButtonDisabled(true);

    const url = getApiUrl(ApiKeys.ADD_AGENCY_DETAILS);
    axiosInstance
      .post(url, request, {
        headers: { donotHandleError: true }
      })
      .then(() => {
        dispatch(setShowLoader(false));
        successCallback();
        toast(showAddAgencyToast(request.agencyName), { type: 'success' });
      })
      .catch(error => {
        dispatch(setShowLoader(false));
        toast(error?.response?.data?.message || 'Agency creation failed', { type: 'error' });
      })
      .finally(() => {
        setIsSubmitButtonDisabled(false);
      });
  };

export const updateAgency =
  (
    referenceId: string,
    request: UpdateAgencyRequest,
    setIsSubmitButtonDisabled: (value: boolean) => void,
    successCallback: () => void
  ) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.UPDATE_AGENCY_DETAILS, { referenceId });
    dispatch(setShowLoader(true));
    setIsSubmitButtonDisabled(true);
    axiosInstance
      .put(url, request, {
        headers: {
          donotHandleError: true
        }
      })
      .then(response => {
        dispatch(setShowLoader(false));
        successCallback();
        toast(showUpdateAgencyToast(request?.agencyName), { type: 'success' });
      })
      .catch(error => {
        dispatch(setShowLoader(false));
        toast('Agency update failed', { type: 'error' });
      })
      .finally(() => {
        setIsSubmitButtonDisabled(false);
      });
  };

export const getAllAgencies = (queryParams?: queryParam) => (dispatch: Dispatch) => {
  dispatch(setAllAgencyApiCalling(true));
  const pageNo = (queryParams?.pageIndex as string | number) || 0;
  const pageSize = (queryParams?.pageSize as string | number) || 20;
  const payload = {
    query: queryParams?.query
  };
  const url = getApiUrl(ApiKeys.GET_ALL_AGENCY, {}, { pageNo, pageSize });

  axiosInstance
    .post(url, payload)
    .then(response => {
      dispatch(setAllAgencyData(response?.data));
    })
    .catch(error => {
      logError('Error inside getAllAgencies API', error);
    })
    .finally(() => {
      dispatch(setAllAgencyApiCalling(false));
    });
};

export const fetchStatesData = (dispatch: Dispatch) => {
  const statesUrl = getApiUrl(ApiKeys.GET_AVAILABLE_STATES);
  axiosInstance.get(statesUrl).then(response => {
    dispatch(setStates({ data: response?.data }));
  });
};

export const getAgencyById = (referenceId: string) => (dispatch: Dispatch) => {
  dispatch(setShowLoader(true));
  const url = getApiUrl(ApiKeys.UPDATE_AGENCY_DETAILS, { referenceId });
  axiosInstance
    .get(url)
    .then(response => {
      dispatch(setAgencyData(response?.data));
    })
    .catch(error => {
      logError('Error in fetching agencies details', error);
    })
    .finally(() => {
      dispatch(setShowLoader(false));
    });
};

export const getAgencyTypes = (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_AGENCY_TYPES_MAPPING);
  axiosInstance
    .get(url)
    .then(response => {
      if (response?.data) {
        const data = response?.data;
        dispatch(setAgencyTypesData(data));

        const agencyTypeMap = {} as Record<string, AgencyTypeOptions>;
        data.forEach((item: AgencyTypeOptions) => {
          const value = item?.value;
          agencyTypeMap[value as string] = item;
        });
        dispatch(setAgencyTypeMappings(agencyTypeMap));
      }
    })
    .catch(err => {
      const errorMessage = err?.response?.data?.message || 'Error fetching agency types';
      toast(errorMessage, { type: 'error' });
    });
};
